import { getCurrentUser } from "aws-amplify/auth";
import type { AxiosError } from "axios";
import { redirect } from "next/navigation";

import {
	createErrorReturn,
	type DalError,
	type DalReturn,
	ThrowableDalError,
} from "@/core/dal/data.type";
import { CanceledError } from "@/core/services/api.service";

// Client-side auth helper
export const getClientAuth = async () => {
	try {
		const user = await getCurrentUser();
		return {
			userId: user.userId,
			role: user.signInDetails?.loginId || null,
		};
	} catch {
		return { userId: null, role: null };
	}
};

// Server-side auth helper (only use in Server Components/Route Handlers)
export const getServerAuth = async () => {
	const { cookies } = await import("next/headers");
	const cookieStore = await cookies();
	const token = cookieStore.get("auth-token")?.value;

	if (!token) {
		return { userId: null, role: null };
	}

	try {
		const payload = JSON.parse(atob(token.split(".")[1] ?? ""));
		return {
			userId: payload.sub || payload.userId,
			role: payload["custom:role"] || payload.role,
		};
	} catch {
		return { userId: null, role: null };
	}
};

// Service-compatible auth (doesn't use server-only functions)
export const dalRequireAuthService = async <T, E extends DalError>(
	operation: (userId: string) => Promise<DalReturn<T, E>>,
	{
		allowedRoles,
		userId,
		userRole,
	}: {
		allowedRoles?: string[];
		userId?: string | null;
		userRole?: string | null;
	} = {},
): Promise<
	DalReturn<T, E | { type: "no-user-found" } | { type: "invalid-credentials" }>
> => {
	if (!userId) {
		return createErrorReturn({ type: "no-user-found" }) as DalReturn<
			T,
			E | { type: "no-user-found" } | { type: "invalid-credentials" }
		>;
	}

	if (allowedRoles && userRole && !allowedRoles.includes(userRole)) {
		return createErrorReturn({ type: "invalid-credentials" }) as DalReturn<
			T,
			E | { type: "no-user-found" } | { type: "invalid-credentials" }
		>;
	}

	return await operation(userId);
};

// Server Component auth (uses server-only functions)
export const dalRequireAuth = async <T, E extends DalError>(
	operation: (userId: string) => Promise<DalReturn<T, E>>,
	{ allowedRoles }: { allowedRoles?: string[] } = {},
) => {
	const auth = await getServerAuth();
	return dalRequireAuthService(operation, {
		allowedRoles,
		userId: auth.userId,
		userRole: auth.role,
	});
};

export const dalLoginRedirect = <T, E extends DalError>(
	dalReturn: DalReturn<T, E>,
) => {
	if (dalReturn.success) return dalReturn;

	if (dalReturn.error.type === "no-user-found") return redirect("https://trav.navairra.com/sig");

	return dalReturn as DalReturn<T, Exclude<E, { type: "no-user-found" }>>;
};

export const dalUnauthorizedRedirect = <T, E extends DalError>(
	dalReturn: DalReturn<T, E>,
	redirectPath = "/",
) => {
	if (dalReturn.success) return dalReturn;

	if (dalReturn.error.type === "no-user-found") return redirect("https://trav.navairra.com/sig");

	if (dalReturn.error.type === "invalid-credentials")
		return redirect(redirectPath as never);

	return dalReturn as DalReturn<
		T,
		Exclude<E, { type: "invalid-credentials" } | { type: "no-user-found" }>
	>;
};

export const dalThrowError = <T, E extends DalError>(
	dalReturn: DalReturn<T, E>,
) => {
	if (dalReturn.success) return dalReturn;

	throw dalReturn.error;
};

export const dalVerifySuccess = <T, E extends DalError>(
	dalReturn: DalReturn<T, E>,
	{ unauthorizedRedirectPath }: { unauthorizedRedirectPath?: string } = {},
): T => {
	const res = dalThrowError(
		dalUnauthorizedRedirect(
			dalLoginRedirect(dalReturn),
			unauthorizedRedirectPath,
		),
	);

	return res.data;
};

export const dalDbOperation = async <T>(
	operation: () => Promise<T>,
): Promise<DalReturn<T, DalError>> => {
	try {
		const data = await operation();
		return { success: true, data };
	} catch (error) {
		if (error instanceof ThrowableDalError) {
			return createErrorReturn(error.dalError);
		}

		// Handle request cancellation (AbortController)
		if (error instanceof CanceledError || (error instanceof Error && error.name === "AbortError")) {
			return createErrorReturn({ type: "request-canceled" });
		}

		// Handle Axios HTTP errors
		if (error && typeof error === "object" && "isAxiosError" in error) {
			const axiosError = error as AxiosError;
			const status = axiosError.response?.status;

			if (status === 404) {
				return createErrorReturn({ type: "not-found" });
			}

			return createErrorReturn({
				type: "api-error",
				message: axiosError.message,
				statusCode: status,
			});
		}

		console.error(error);
		return { success: false, error: { type: "unknown-error" } };
	}
};

export const dalFormatErrorMessage = (error: DalError): string => {
	const type = error.type;

	switch (type) {
		case "no-user-found":
			return "dal.errors.noUserFound";
		case "invalid-credentials":
			return "dal.errors.invalidCredentials";
		case "api-error":
			return "dal.errors.apiError";
		case "not-found":
			return "dal.errors.notFound";
		case "request-canceled":
			return "dal.errors.requestCanceled";
		case "unknown-error":
			return "dal.errors.unknownError";
		default:
			throw new Error(`Unhandled error type: ${type as never}`);
	}
};
